import { memo, useCallback, useMemo, useState } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import { useDerivedValue } from 'react-native-reanimated';

import { useFocusEffect } from '@react-navigation/native';

import {
    EventsInfoWithFilteredPlayersDocument,
    EventsInfoWithFilteredPlayersQuery,
    EventsInfoWithFilteredPlayersQueryVariables,
} from '@/api/events/query.generated';
import { DoubleArrow } from '@/assets/icons/DoubleArrow';
import { Button } from '@/components/ButtonComponent';
import { LottieLoader } from '@/components/LottieLoader';
import { ReanimatedAccordion } from '@/components/ReanimatedAccordion';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components/Box';
import { Row } from '@/components/lib/components/Row';
import { ds } from '@/components/lib/design.system';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import { UserAvatar } from '@/feature/account-details/components/UserAvatar';
import { common, withOpacity } from '@/styles/styles';
import { SocialFeedEntry } from '@/types/socialFeed.generated';
import { logger } from '@/utils/logging';
import { useClient } from 'urql';

import { useAlerts } from '../alerts/hooks/use-alerts';
import { useBetslipStore } from '../betslip-pickem/hooks/use-betslip-store';
import { useAddLineupToBetslip } from '../betslip-pickem/hooks/useAddLineupToBetslip';
import {
    getMatchingBetslipPicks,
    isPrebuiltLineupInBetslip,
    mapFeedEntryToBetslipPick,
} from '../betslip-pickem/utils/betslip-utils';
import { createPlayerEventKey } from '../betslip-pickem/utils/markets-cache-store-utils';
import { getEntryTitle } from '../entries-pickem/entries-utils';
import { useEventPlayerIds } from '../entries-pickem/hooks/use-event-player-ids';
import { PlayerRowSmall } from '../lobby/components/PlayerRowSmall';
import { getFirstStartingEvent, timeAgo, useCurrentTime, useEventCountdown } from './utils';

const PREVIEW_COUNT = 4;
const PLAYER_ROW_HEIGHT = 58;

const EXPANDED_HEIGHT_OFFSET = 10;

type Props = {
    feedEntry: SocialFeedEntry;
};

const PicksList = memo(({ picks }: { picks: SocialFeedEntry['picks'] }) => {
    return picks.map(pick => (
        <Box marginTop={'s16'} key={createPlayerEventKey(pick.player.id, pick.eventId)}>
            <PlayerRowSmall
                player={{ ...pick.player, league: pick.league }}
                projection={pick.projection}
                outcome={pick.outcome}
                opponentLabel={pick.opponentDisplay}
                hideCrossedOutValue
            />
        </Box>
    ));
});

export const SocialFeedCard = memo(({ feedEntry }: Props) => {
    const nearestEvent = useMemo(() => getFirstStartingEvent(feedEntry.picks), [feedEntry.picks]);
    const betslip = useBetslipStore(state => state.betslip);
    const removePicks = useBetslipStore(state => state.actions.removePicks);
    const [expanded, setExpanded] = useState(false);
    const expandedDerived = useDerivedValue(() => expanded, [expanded]);
    const { width } = useWindowDimensions();

    const initialPicks = useMemo(() => feedEntry.picks.slice(0, PREVIEW_COUNT), [feedEntry.picks]);
    const extraPicks = useMemo(() => feedEntry.picks.slice(PREVIEW_COUNT), [feedEntry.picks]);
    const createdDate = feedEntry.createdDate;
    const timeAgoText = useMemo(() => timeAgo(createdDate), [createdDate]);

    const shouldShowSeeAll = feedEntry.picks.length >= PREVIEW_COUNT;

    const addedToBetslip = useMemo(() => {
        return isPrebuiltLineupInBetslip(betslip, feedEntry.picks);
    }, [betslip, feedEntry.picks]);
    const picksToRemove = useMemo(() => {
        return getMatchingBetslipPicks(betslip, feedEntry.picks);
    }, [betslip, feedEntry.picks]);

    const { addLineup } = useAddLineupToBetslip();
    const { eventIds, playerIds } = useEventPlayerIds(feedEntry.picks);
    const gqlClient = useClient();
    const { showToast } = useAlerts();
    const [fetching, setFetching] = useState(false);

    const fetchEventsAndAddLineup = useCallback(async () => {
        setFetching(true);
        try {
            const response = await gqlClient.query<
                EventsInfoWithFilteredPlayersQuery,
                EventsInfoWithFilteredPlayersQueryVariables
            >(EventsInfoWithFilteredPlayersDocument, { eventIds, playerIds }, { requestPolicy: 'network-only' });

            const events = response.data?.getEventsWithFilteredPlayers ?? [];
            if (events.length > 0) {
                const betslipPicks = mapFeedEntryToBetslipPick(feedEntry.picks, events);
                addLineup(betslipPicks, { socialFeedEntryId: feedEntry.socialFeedEntryId });
            }
        } catch (error) {
            showToast({ message: 'Failed to add lineup. Please try again.', toastType: 'warning' });
            logger.warn('[SocialFeed]', 'Failed to fetch events', error);
        } finally {
            setFetching(false);
        }
    }, [addLineup, eventIds, feedEntry.picks, feedEntry.socialFeedEntryId, gqlClient, playerIds, showToast]);

    const addOrRemoveLineupFromBetslip = useCallback(() => {
        if (fetching) {
            return;
        }
        if (addedToBetslip) {
            removePicks(picksToRemove);
            return;
        }
        fetchEventsAndAddLineup();
    }, [addedToBetslip, fetchEventsAndAddLineup, fetching, picksToRemove, removePicks]);

    const handleExpand = useCallback(() => {
        setExpanded(prev => !prev);
    }, []);

    useFocusEffect(
        useCallback(() => {
            // on screen blur, collapse the expanded picks
            return () => {
                setExpanded(false);
            };
        }, [])
    );

    return (
        <Box backgroundColor={ds.colorToken.background.neutral.subtler} borderRadius={'r24'} style={styles.container}>
            <Row gap={'s12'}>
                <UserAvatar avatarUrl={feedEntry.avatarUrl} avatarContainerStyles={styles.avatarContainer} />
                <Box>
                    <Text variant={'titleMedium'} color={ds.colorToken.text.primary} lineHeight={20}>
                        @{feedEntry.title}
                    </Text>
                    <Text variant={'bodySmall'} color={ds.colorToken.text.secondary} letterSpacing={-0.08}>
                        {timeAgoText}
                    </Text>
                </Box>
            </Row>
            <Box backgroundColor={ds.colorToken.background.neutral.subtler} borderRadius={'r20'} style={styles.content}>
                <Row alignItems={'center'} gap={'s2'}>
                    <Text variant={'titleMedium'} color={ds.colorToken.text.primary}>
                        {`${feedEntry.picks?.length}-Pick ${getEntryTitle(feedEntry.gameMode)} Play`}
                    </Text>
                    <DoubleArrow />
                    <Text variant={'titleMedium'} color={ds.colorToken.text.brand}>
                        {feedEntry.multiplier}x
                    </Text>
                </Row>
                <FirstGameTimer startDate={nearestEvent?.eventDate} />
                <Box>
                    <PicksList picks={initialPicks} />
                    {shouldShowSeeAll ? (
                        <>
                            <ReanimatedAccordion
                                duration={200}
                                expanded={expandedDerived}
                                contentHeight={PLAYER_ROW_HEIGHT * extraPicks.length + EXPANDED_HEIGHT_OFFSET}
                            >
                                <PicksList picks={extraPicks} />
                            </ReanimatedAccordion>
                            {expanded ? null : (
                                <Box
                                    height={64}
                                    paddingTop={'s20'}
                                    paddingBottom={'s16'}
                                    style={styles.buttonContainer}
                                    alignItems={'center'}
                                    overflow={'hidden'}
                                >
                                    <LinearGradient
                                        colors={[
                                            withOpacity(ds.colorValue.background.neutral.subtler, 0),
                                            withOpacity(ds.colorValue.background.neutral.subtler, 0.8),
                                            ds.colorValue.background.neutral.subtler,
                                        ]}
                                        start={{ x: 0, y: 0 }}
                                        end={{ x: 0, y: 1 }}
                                        positions={[0, 0.5, 0.75]}
                                        height={64}
                                        width={width}
                                        style={[styles.gradient, common.zIndex1]}
                                    />
                                    <Button
                                        size={'xs'}
                                        hierarchy={'primary'}
                                        onPress={handleExpand}
                                        label={`See all (${feedEntry.picks.length})`}
                                    />
                                </Box>
                            )}
                        </>
                    ) : null}
                </Box>
                <Box paddingTop={shouldShowSeeAll ? 's14' : 's24'}>
                    <Button
                        size={'s'}
                        label={
                            fetching ? (
                                <LottieLoader size={24} color={ds.colorValue.icon.primary} />
                            ) : (
                                <Row gap={'s8'}>
                                    <Text variant={'titleMedium'} color={ds.colorToken.text.primary}>
                                        {addedToBetslip ? 'Remove' : 'Add Lineup'}
                                    </Text>
                                </Row>
                            )
                        }
                        hierarchy={'primaryLilac'}
                        variant={addedToBetslip ? 'light' : undefined}
                        onPress={addOrRemoveLineupFromBetslip}
                        testID={`social-feed-add-remove-lineup-button`}
                    />
                </Box>
            </Box>
        </Box>
    );
});

const FirstGameTimer = ({ startDate }: { startDate?: string }) => {
    const currentTime = useCurrentTime(true);
    const countdown = useEventCountdown(currentTime, startDate);

    return (
        <Text variant={'bodySmall'} color={ds.colorToken.text.secondary} letterSpacing={-0.08}>
            {'First game starts in '}
            <Text variant={'bodySmall'} color={ds.colorToken.text.accent.yellow}>
                {countdown}
            </Text>
        </Text>
    );
};

const styles = StyleSheet.create({
    container: {
        borderWidth: 1,
        borderCurve: 'continuous',
        borderColor: ds.colorValue.border.subtle,
        gap: 16,
        padding: 15,
    },
    content: {
        borderWidth: 1,
        borderCurve: 'continuous',
        borderColor: ds.colorValue.border.strong,
        padding: 15,
    },
    gradient: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        height: 64,
    },
    buttonContainer: {
        position: 'absolute',
        bottom: -6,
        left: 0,
        right: 0,
        justifyContent: 'center',
        alignItems: 'center',
    },
    avatarContainer: {
        backgroundColor: ds.colorValue.background.neutral.subtle,
        borderColor: ds.colorValue.border.subtle,
    },
});
